import {
  BasePagination,
  Button,
  Checkbox,
  HStack,
  Text,
  useBoolean,
  useCustomToast,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import {
  ICrmBrandTradeLicenseMappingData,
  useBulkToggleVerificationApi,
  useGetCrmBrandTradeLicenseMappingsApi,
  useToggleVerificationApi,
} from 'apis/crm-brand-trade-license-mappings';
import { BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableHeader,
} from 'components/base-table/components';

import { SortMenuButton } from 'components/base-table/sorting';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import React, { useEffect, useState } from 'react';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { EntityBrandFilterButton } from '../components/filters/entity-brand-filter';
import { LicenseDedFilterButton } from '../components/filters/license-ded-filter';
import { VerificationStatusFilterButton } from '../components/filters/verification-status-filter';
import { BulkActionsModal } from '../components/modals/bulk-actions-modal';
import { EditMappingModal } from '../components/modals/edit-mapping-modal';
import { LicenseMappingModal } from '../components/modals/license-mapping-modal';
import { ViewMappingModal } from '../components/modals/view-mapping-modal';
import {
  ActionsCell,
  BrandCell,
  DedNameCell,
  EntityNameCell,
  TradeLicenseNameCell,
  VerificationCell,
} from '../components/table-cells';

const CrmBrandTradeLicenseMappingsListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();
  const toast = useCustomToast();

  // Modals state
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isEditBrandOpen, onOpen: onEditBrandOpen, onClose: onEditBrandClose } = useDisclosure();
  const { isOpen: isEditLicenseOpen, onOpen: onEditLicenseOpen, onClose: onEditLicenseClose } = useDisclosure();
  const [selectedMappingId, setSelectedMappingId] = useState<number | null>(null);
  const [selectedMappingData, setSelectedMappingData] = useState<{
    entityName: string;
    tradeLicenseName: string;
    licenseNumber?: string;
  } | null>(null);

  // API hooks
  const { isLoading, isFetching, data } = useGetCrmBrandTradeLicenseMappingsApi({
    onError: () => {
      toast({
        title: 'Failed to Load Data',
        description: 'Unable to load trade license mappings. Please check your connection and try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  const { mutate: toggleVerification, isLoading: isTogglingVerification } = useToggleVerificationApi();
  const { mutateAsync: bulkToggleVerification } = useBulkToggleVerificationApi();

  const { pageCount, total, data: mappingsData } = data ?? {};
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isSelecting, setIsSelecting] = useBoolean(false);
  const { isOpen: isBulkOpen, onOpen: onBulkOpen, onClose: onBulkClose } = useDisclosure();

  // Handlers
  const handleViewMapping = (mappingId: number) => {
    setSelectedMappingId(mappingId);
    onViewOpen();
  };

  const handleEditBrandMapping = (mappingId: number) => {
    setSelectedMappingId(mappingId);
    onEditBrandOpen();
  };

  const handleEditLicenseMapping = (
    mappingId: number,
    entityName: string,
    tradeLicenseName: string,
    licenseNumber?: string
  ) => {
    setSelectedMappingId(mappingId);
    setSelectedMappingData({ entityName, tradeLicenseName, licenseNumber });
    onEditLicenseOpen();
  };

  const handleToggleVerification = (id: number, isVerified: boolean) => {
    toast({
      title: `${isVerified ? 'Verifying' : 'Un-verifying'} Mapping...`,
      description: `Please wait while we update the verification status.`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });

    toggleVerification({ id, isVerified });
  };

  const handleBulkAction = async (action: string, mappingIds: number[]) => {
    if (!mappingIds.length) return;

    try {
      if (action === 'verify' || action === 'unverify') {
        const desired = action === 'verify';
        await bulkToggleVerification({ body: { ids: mappingIds, isVerified: desired } });

        toast({
          title: 'Bulk Verification Complete',
          description: `Applied "${desired ? 'Verify' : 'Unverify'}" to ${mappingIds.length} mappings`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        setSelectedIds([]);
        onBulkClose();
        return;
      }

      toast({
        title: 'Action Not Implemented',
        description: 'Only Verify/Unverify are supported for now.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: 'Bulk Action Failed',
        description: 'Some items may not have been updated. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Set page title
  useEffect(() => {
    dispatch(changeTitle('CRM Brand Trade License Mappings'));
  }, [dispatch]);

  const columns = React.useMemo(
    () => [
      // Selection column
      {
        id: 'selection',
        Header: (
          <HStack spacing={2} align='center'>
            <Checkbox
              isChecked={selectedIds.length > 0 && selectedIds.length === (mappingsData?.length || 0)}
              isIndeterminate={selectedIds.length > 0 && selectedIds.length < (mappingsData?.length || 0)}
              onChange={(e) => {
                if (e.target.checked) setSelectedIds((mappingsData || []).map((m) => m.id));
                else setSelectedIds([]);
              }}
            />
            <Text fontSize='sm' color='gray.600'>
              All
            </Text>
          </HStack>
        ),
        accessor: 'selection',
        width: '4%',
        disableSortBy: true,
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={() => value} />,
      },
      {
        Header: <TableHeader name='Entity & Brand' filterButtonComponent={<EntityBrandFilterButton />} />,
        accessor: 'entityAndBrand',
        width: '30%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={value.as} {...value.props} />,
      },
      {
        Header: <TableHeader name='Trade License & DED' filterButtonComponent={<LicenseDedFilterButton />} />,
        accessor: 'licenseAndDed',
        width: '30%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={value.as} {...value.props} />,
      },
      {
        Header: <TableHeader name='Verification Status' filterButtonComponent={<VerificationStatusFilterButton />} />,
        accessor: 'verification',
        width: '15%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={VerificationCell} {...value} />,
      },
      {
        Header: <TableHeader name='Created At' />,
        accessor: 'createdAt',
        width: '12%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={() => <Text fontSize='sm'>{value}</Text>} />,
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        width: '15%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={ActionsCell} {...value} />,
      },
    ],
    [isTogglingVerification, selectedIds, mappingsData]
  );

  const tableData = React.useMemo(
    () =>
      mappingsData?.map((item: ICrmBrandTradeLicenseMappingData) => ({
        id: item.id,
        selection: (
          <Checkbox
            isChecked={selectedIds.includes(item.id)}
            onChange={(e) => {
              setSelectedIds((prev) =>
                e.target.checked ? [...new Set([...prev, item.id])] : prev.filter((id) => id !== item.id)
              );
            }}
          />
        ),
        entityAndBrand: {
          as: ({ entityName, brand, brandConfidence }: any) => (
            <VStack align='start' spacing={2} maxW='100%'>
              <CustomTableCell as={EntityNameCell} entityName={entityName} />
              <CustomTableCell as={BrandCell} brand={brand} brandConfidence={brandConfidence} />
            </VStack>
          ),
          props: {
            entityName: item.entityName,
            brand: item.brand,
            brandConfidence: item.brandConfidence,
          },
        },
        licenseAndDed: {
          as: ({ tradeLicenseName, licenseConfidence, dedDetail }: any) => (
            <VStack align='start' spacing={2} maxW='100%'>
              <CustomTableCell as={TradeLicenseNameCell} tradeLicenseName={tradeLicenseName} />
              <CustomTableCell
                as={DedNameCell}
                businessName={dedDetail?.businessName || ''}
                licenseConfidence={licenseConfidence}
              />
            </VStack>
          ),
          props: {
            tradeLicenseName: item.tradeLicenseName,
            licenseConfidence: item.licenseConfidence ?? 0,
            dedDetail: item.dedDetail,
          },
        },
        verification: {
          id: item.id,
          isVerified: item.isVerified,
          onToggle: handleToggleVerification,
          isLoading: isTogglingVerification,
        },
        createdAt: new Date(item.createdAt).toLocaleDateString(),
        actions: {
          mappingId: item.id,
          entityName: item.entityName,
          currentLicenseNumber: item.licenseNumber,
          onView: handleViewMapping,
          onEditBrand: handleEditBrandMapping,
          onEditLicense: (mappingId: number) =>
            handleEditLicenseMapping(mappingId, item.entityName, item.tradeLicenseName, item.licenseNumber),
        },
      })) || [],
    [mappingsData, isTogglingVerification, handleToggleVerification, selectedIds]
  );

  return (
    <>
      <BaseTableContainer isLoading={isLoading}>
        <BaseTableContainer.Header>
          <BaseTableContainer.Header.Left>
            <BaseTableTitle title='CRM Brand Trade License Mappings' />
            <BaseTableSpinner isFetching={isFetching} />
          </BaseTableContainer.Header.Left>

          <BaseTableContainer.Header.Right>
            <HStack spacing='sm'>
              <SortMenuButton />
              <ClearFilterButton />
            </HStack>
          </BaseTableContainer.Header.Right>
        </BaseTableContainer.Header>

        <BaseTableContainer.TableWrapper>
          <BaseTable
            columns={columns}
            data={tableData}
            size='sm'
            variant='striped'
            colorScheme='gray'
            stickyHeader
            rowHover
          />
        </BaseTableContainer.TableWrapper>

        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left>
            <HStack spacing={3} align='center'>
              <Button size='sm' variant='outline' onClick={setIsSelecting.toggle}>
                {isSelecting ? 'Done Selecting' : 'Select Rows'}
              </Button>
              <Button size='sm' colorScheme='blue' isDisabled={!selectedIds.length} onClick={() => setSelectedIds([])}>
                Clear Selection
              </Button>
              <Button size='sm' colorScheme='green' isDisabled={!selectedIds.length} onClick={onBulkOpen}>
                Bulk Actions
              </Button>
              <Text fontSize='sm' color='gray.600'>
                {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'No selection'}
              </Text>
            </HStack>
          </BaseTableContainer.Footer.Left>
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      </BaseTableContainer>

      {/* Modals */}
      {selectedMappingId && (
        <>
          <ViewMappingModal isOpen={isViewOpen} onClose={onViewClose} mappingId={selectedMappingId} />
          <EditMappingModal isOpen={isEditBrandOpen} onClose={onEditBrandClose} mappingId={selectedMappingId} />
          {selectedMappingData && (
            <LicenseMappingModal
              isOpen={isEditLicenseOpen}
              onClose={onEditLicenseClose}
              mappingId={selectedMappingId}
              entityName={selectedMappingData.entityName}
              tradeLicenseName={selectedMappingData.tradeLicenseName}
              currentLicenseNumber={selectedMappingData.licenseNumber}
            />
          )}
        </>
      )}

      {/* Bulk Actions Modal */}
      <BulkActionsModal
        isOpen={isBulkOpen}
        onClose={onBulkClose}
        selectedMappings={selectedIds}
        onBulkAction={handleBulkAction}
      />
    </>
  );
};

export default CrmBrandTradeLicenseMappingsListScreen;
